import React, { useState } from "react";

import moment from "moment";

import { Property } from "manager/interfaces/api";
import { DatePicker, TextArea } from "shared/components/Form";
import { UnderlineLink } from "shared/components/Links";
import { Modal } from "shared/components/Modal";
import useModal from "shared/hooks/useModal";

import {
  BILLING_REASON_MAX_LENGTH,
  formatBillingDate,
  goLiveWhenKind,
  propertyUnitCount,
  STOP_REASON_OTHER,
  STOP_REASONS,
  STOP_BILLING_SUBTITLE,
  stopBillingHint,
  stopBillingReasonFromSelection,
  stopBillingSubmitLabel,
  todayDateInput,
  toBillingWriteAt,
} from "./billingDisplay";
import {
  DateActionRow,
  DateField,
  DateFieldLabel,
  DatePickerWrap,
  DateShortcuts,
  PropertySummary,
  PropertySummaryName,
  PropertySummaryUnits,
  ReasonChip,
  ReasonRow,
  RestartHint,
  SetLiveBody,
} from "./styled";

interface StopBillingModalProps {
  property: Property;
  submit: (payload: {
    billingStoppedAt: string;
    reason: string;
  }) => Promise<void> | void;
  submitting?: boolean;
}

const StopBillingModal = ({
  property,
  submit,
  submitting = false,
}: StopBillingModalProps) => {
  const { closeActiveModalDialog } = useModal();
  const [date, setDate] = useState(todayDateInput());
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const kind = goLiveWhenKind(date);
  const reason = stopBillingReasonFromSelection(selectedReason, otherReason);
  const canSubmit = Boolean(date && kind !== "past" && reason);

  return (
    <Modal
      title={`Stop billing ${property.name}?`}
      subtitle={STOP_BILLING_SUBTITLE}
      submit={() =>
        submit({
          billingStoppedAt: toBillingWriteAt(date),
          reason,
        })
      }
      submitButtonLabel={stopBillingSubmitLabel(date)}
      submitButtonDisabled={!canSubmit}
      submitting={submitting}
      closeOnSubmit={false}
      afterClose={closeActiveModalDialog}
      showCancelLink
      width={560}
    >
      <Modal.Body>
        <SetLiveBody>
          <PropertySummary>
            <PropertySummaryName>{property.name}</PropertySummaryName>
            <PropertySummaryUnits>
              {propertyUnitCount(property)} units · live since{" "}
              {formatBillingDate(property.billingLiveAt)}
            </PropertySummaryUnits>
          </PropertySummary>
          <DateField>
            <DateFieldLabel>Stop date</DateFieldLabel>
            <DateActionRow>
              <DatePickerWrap>
                <DatePicker
                  id="billingStoppedAt"
                  name="billingStoppedAt"
                  label=""
                  value={date}
                  onChange={setDate}
                  displayFormat="MM/DD/YYYY"
                  disabledDate={(current) => current.isBefore(moment(), "day")}
                />
              </DatePickerWrap>
              <DateShortcuts>
                <UnderlineLink
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setDate(todayDateInput());
                  }}
                >
                  Today
                </UnderlineLink>
              </DateShortcuts>
            </DateActionRow>
            <RestartHint>{stopBillingHint(date)}</RestartHint>
          </DateField>
          <div>
            <DateFieldLabel>Reason</DateFieldLabel>
            <ReasonRow>
              {STOP_REASONS.map((label) => (
                <ReasonChip
                  key={label}
                  type="button"
                  active={selectedReason === label}
                  onClick={() => {
                    setSelectedReason(label);
                    if (label !== STOP_REASON_OTHER) {
                      setOtherReason("");
                    }
                  }}
                >
                  {label}
                </ReasonChip>
              ))}
            </ReasonRow>
            {selectedReason === STOP_REASON_OTHER && (
              <TextArea
                id="stopOtherReason"
                name="stopOtherReason"
                label="Reason (required)"
                required
                maxLength={BILLING_REASON_MAX_LENGTH}
                value={otherReason}
                onChange={(event) => setOtherReason(event.target.value)}
              />
            )}
          </div>
          <RestartHint>
            Once stopped, the property is inactive and shows{" "}
            <strong>Not live</strong> (not Onboarding). History is kept, so it
            can be set live again later.
          </RestartHint>
        </SetLiveBody>
      </Modal.Body>
    </Modal>
  );
};

export default StopBillingModal;
